import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { LanguageCode, MarketCode } from '@tink/config/src/markets';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Header } from '../../../../../../organisms/Header/Header';
import { Button } from '../../../../../../atoms/Button/Button';
import { Footer } from '../../../../../../atoms/Footer/Footer';
import { ScopeItem } from '../../../../../../../lib-api/services/Oauth';
import { ContentBody } from '../../../../../../atoms/ContentBody/ContentBody';
import { ContentOverlay } from '../../../../../../molecules/ContentOverlay/ContentOverlay';

import { useAnalytics } from '../../../../../../../core/Analytics/AnalyticsProvider';
import { PageView } from '../../../../../../../models/analytics';
import { Text } from '../../../../../../atoms/Text/Text';
import { H1, H2, H3 } from '../../../../../../atoms/Heading/Heading';
import { ScreenWrapper } from '../../../../../../atoms/ScreenWrapper/ScreenWrapper';
import { AppState, useAppSelector } from '../../../../../../../stores/redux-store';
import { User } from '../../../../../../../models/user';
import { useUrlQuery } from '../../../../../../ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { getLanguageCode } from '../../../../../../../utilities/utils';
import { DocumentOverlayLink } from '../../../molecules/DocumentOverlayLink/DocumentOverlayLink';
import { getTermsAndConditionsMarketUrl } from '../../../../../../../core/screens/Legal/TermsAndConditions/TermsAndConditionsMarket';
import { getPrivacyPolicyMarketUrl } from '../../../../../../../core/screens/Legal/PrivacyPolicy/PrivacyPolicyMarket';
import { messages } from './messages';

export type ModalContentProps = {
  scopes: ScopeItem[];
  clientName?: string;
  market: MarketCode;
  isTemporaryUser?: boolean;
  languageCode?: LanguageCode;
};

const Anchor = styled.a`
  color: ${(props) => props.theme.palette.black.main};
`;
const FlexRow = styled.div`
  display: flex;
  white-space: break-spaces;
`;

export const ModalContent: React.FC<ModalContentProps> = ({
  scopes,
  clientName = null,
  market,
  isTemporaryUser = true,
  languageCode = LanguageCode.en,
}) => {
  return (
    <>
      <H1>
        <FormattedMessage {...messages.headline} />
      </H1>
      {market === MarketCode.US ? (
        <Text mb="medium">
          <FormattedMessage {...messages.aggregationPartnerBody} values={{ appName: clientName }} />
        </Text>
      ) : (
        <Text mb="medium">
          <FormattedMessage {...messages.aboutYourDataBody} values={{ appName: clientName }} />
        </Text>
      )}
      <H2>
        <FormattedMessage {...messages.headline2} />
      </H2>
      <Text mb="medium">
        <FormattedMessage {...messages.dataCollectionBody1} values={{ appName: clientName }} />
      </Text>
      <H2>
        <FormattedMessage {...messages.headline3} />{' '}
      </H2>
      {scopes.map((scope, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          <H3 mt="none" mb="xxsmall">
            {scope.title}
          </H3>
          <Text mb="small">{scope.description}</Text>
        </React.Fragment>
      ))}
      {market === MarketCode.US && (
        <>
          <H2>
            <FormattedMessage {...messages.headline4} />
          </H2>
          <Text mb="medium">
            <FormattedMessage {...messages.dataCollectionBody2} />
          </Text>
        </>
      )}

      <H2>
        <FormattedMessage {...messages.headline5} />
      </H2>
      <H3 mt="none" mb="xxsmall">
        <FormattedMessage {...messages.subheadline1} />
      </H3>
      {isTemporaryUser ? (
        <Text mb="small">
          <FormattedMessage {...messages.dataSubCollectionBody1Temporary} />
        </Text>
      ) : (
        <Text mb="small">
          <FormattedMessage {...messages.dataSubCollectionBody1Permanent} />
        </Text>
      )}
      <H3 mt="none" mb="xxsmall">
        <FormattedMessage {...messages.subheadline2} />
      </H3>
      <Text mb="medium">
        {isTemporaryUser ? (
          <FormattedMessage
            {...messages.dataSubCollectionBody2Temporary}
            values={{
              appName: clientName,
            }}
          />
        ) : (
          <FormattedMessage
            {...messages.dataSubCollectionBody2Permanent}
            values={{
              appName: clientName,
              consentEmailHref: <Anchor href="mailto:contact@tink.com">contact@tink.com</Anchor>,
            }}
          />
        )}
      </Text>
      <FlexRow>
        <DocumentOverlayLink
          documentUrl={getTermsAndConditionsMarketUrl({ market, language: languageCode })}
          pageView={PageView.TERMS_AND_CONDITION_SCREEN}
        >
          <FormattedMessage {...messages.termsAndConditions} />
        </DocumentOverlayLink>
        {/* eslint-disable-next-line react/jsx-no-literals */}
        <span> & </span>
        <DocumentOverlayLink
          documentUrl={getPrivacyPolicyMarketUrl({ market, language: languageCode })}
          pageView={PageView.PRIVACY_POLICY_SCREEN}
        >
          <FormattedMessage {...messages.privacyPolicy} />
        </DocumentOverlayLink>
      </FlexRow>
    </>
  );
};

type ScopesDetailOverlayProps = {
  scopes: ScopeItem[];
  close: () => void;
};

export const ScopesDetailOverlay = ({ close, scopes }: ScopesDetailOverlayProps) => {
  const { locale, market } = useUrlQuery();
  const languageCode = getLanguageCode(locale);
  const ref = useRef<HTMLDivElement>(null);
  const { tinklinkAnalytics } = useAnalytics();

  const clientName = useAppSelector((state) => state.oauth.description?.clientName);
  const user = useSelector((state: AppState) => state.user.user);

  useEffect(() => {
    tinklinkAnalytics.sendViewEvent({ view: PageView.SCOPES_DETAIL_SCREEN });
  }, [tinklinkAnalytics]);

  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
    }
  }, [ref]);

  return (
    <ContentOverlay>
      <ScreenWrapper
        role="dialog"
        aria-modal="true"
        aria-describedby="scopes-details"
        tabIndex={0}
        ref={ref}
      >
        <Header onBack={close} hideCloseButton pageView={PageView.SCOPES_DETAIL_SCREEN} />

        <ContentBody id="scopes-details">
          <ModalContent
            scopes={scopes}
            clientName={clientName}
            market={market}
            isTemporaryUser={User.isTemporary(user)}
            languageCode={languageCode}
          />
          <Footer stickToBottom>
            <Button color="primary" onClick={close} variant="contained">
              <FormattedMessage {...messages.close} />
            </Button>
          </Footer>
        </ContentBody>
      </ScreenWrapper>
    </ContentOverlay>
  );
};
